import React, { SyntheticEvent } from "react";
import { StyleProp, StyleSheet, TextInput, TextInputProps, TextStyle, View, ViewProps, ViewStyle } from "react-native";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";

import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";

import "src/util/datePicker.css";
import { PdText, tPdText } from "src/components/layout";
import { PdDivider } from "src/components/v2/ui/core";
import { PdClose } from "src/components/Icons";
import { PdButton, PdButtonLabel } from "./button";

export type tPdInputProps = TextInputProps & { preventSpaces?: boolean; isDisabled?: boolean };

export type tPdInputMultipleProps = Omit<tPdInputProps, "value" | "onChangeText"> & {
  value?: string[] | null;
  onChangeText?: (value: string[]) => void;
};

export type tPdInputNumberProps = Omit<tPdInputProps, "value" | "onChangeText"> & {
  value?: number | null;
  onChangeNumber?: (value: number | null) => void;
};

export type tPdInputFloatProps = tPdInputProps;

export type tPdInputPercentageProps = Omit<tPdInputNumberProps, "value" | "onChangeText"> & {
  value?: number | null;
  onChangeNumber?: (value: number | null) => void;
  isDisabled?: boolean;
};

export type tPdRangeDateInputProps = Omit<ReactDatePickerProps, "disabled"> & {
  period: [Date, Date];
  isDisabled?: boolean;
  style?: StyleProp<ViewStyle>;
  textInputStyle?: StyleProp<TextStyle>;
};

export const PdInputGroup = React.forwardRef<View, React.PropsWithChildren<ViewProps>>((props, ref) => {
  const { style, ...rest } = props;
  return <View ref={ref} style={[styles.pdInputGroup, style]} {...rest} />;
});

export const PdInputLeftAddon: React.FC<tPdText> = ({ style, ...rest }) => {
  return <PdText style={[styles.pdInputLeftAddon, style]} {...rest} />;
};

export const PdInputRightAddon: React.FC<ViewProps> = ({ style, ...rest }) => {
  return <View style={[styles.pdInputRightAddon, style]} {...rest} />;
};

export const PdInput = React.forwardRef<TextInput, tPdInputProps>((props, ref) => {
  const { style, preventSpaces, isDisabled, onChangeText, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (!isDisabled) {
      const _text = preventSpaces ? text.replace(/\s+/g, "") : text;
      onChangeText?.(_text);
    }
  };

  return (
    <TextInput
      ref={ref}
      style={[styles.pdInput, isDisabled ? styles.pdDisabledInput : {}, style]}
      onChangeText={handleOnChangeText}
      editable={!isDisabled}
      {...rest}
    />
  );
});

const INPUT_MULTIPLE_SEPARATOR = ",";

export const PdInputMultiple = React.forwardRef<TextInput, tPdInputMultipleProps>((props, ref) => {
  const { value, onChangeText, placeholder, style, ...rest } = props;
  const [newTag, setNewTag] = React.useState("");

  const safeValue = value || [];

  const handleOnAdd = (maybeNewValue: string[]) => {
    const maybeAddedValue = safeValue.find((item) => maybeNewValue.includes(item));
    if (maybeAddedValue) return;
    onChangeText?.([...safeValue, ...maybeNewValue]);
  };

  const handleOnChangeNewTag: tPdInputProps["onChangeText"] = (_newTag) => {
    if (_newTag.includes(INPUT_MULTIPLE_SEPARATOR)) {
      handleOnAdd(_newTag.split(INPUT_MULTIPLE_SEPARATOR).filter((split) => split));
      setNewTag("");
      return;
    }

    setNewTag(_newTag);
  };

  const handleOnKeyPress: tPdInputProps["onKeyPress"] = (event) => {
    if (event.nativeEvent.key === "Enter" && newTag) {
      handleOnAdd([newTag]);
      setNewTag("");
      return;
    }

    if (event.nativeEvent.key === "Backspace" && newTag === "") {
      onChangeText?.(safeValue.slice(0, -1));
    }
  };

  const handleOnRemove = (itemToRemove: string) => {
    onChangeText?.(safeValue.filter((item) => item !== itemToRemove));
  };

  return (
    <PdInputGroup style={[styles.pdInputMultiple, style]} {...rest}>
      {safeValue.map((item) => (
        <PdButton
          style={styles.pdInputMultipleItem}
          colorScheme="pink"
          size="sm"
          key={item}
          rightIcon={<PdClose style={styles.pdInputMultipleItemRemoveIcon} />}
          onPress={() => handleOnRemove(item)}
        >
          <PdButtonLabel>{item}</PdButtonLabel>
        </PdButton>
      ))}
      <PdInput
        ref={ref}
        style={styles.pdInputGroupInput}
        value={newTag}
        onChangeText={handleOnChangeNewTag}
        onKeyPress={handleOnKeyPress}
        placeholder={placeholder}
        blurOnSubmit={false}
      />
    </PdInputGroup>
  );
});

export const PdInputNumber = React.forwardRef<TextInput, tPdInputNumberProps>((props, ref) => {
  const { value, onChangeNumber, maxLength, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (/^[0-9]*$/gm.test(text)) {
      if (text.charAt(0) === "0" && text.length > 1) text = text.substring(1);
      if (!maxLength || text.length < maxLength) {
        onChangeNumber?.(text.length === 0 ? null : Number.parseFloat(text));
      }
    }
  };

  return (
    <PdInput
      ref={ref}
      value={value === null || value === undefined ? "" : `${value}`}
      onChangeText={handleOnChangeText}
      keyboardType="numeric"
      maxLength={maxLength}
      {...rest}
    />
  );
});

export const PdInputFloat = React.forwardRef<TextInput, tPdInputFloatProps>((props, ref) => {
  const { value, onChangeText, ...rest } = props;

  const handleOnChangeText = (text: string) => {
    if (/^[0-9]*\.?[0-9]*$/gm.test(text)) {
      if (text.charAt(0) === "0" && text.length > 1) text = text.substring(1);
      else if (!text.length) text = "0";
      onChangeText?.(text);
    }
  };

  return <PdInput ref={ref} value={value} onChangeText={handleOnChangeText} keyboardType="numeric" {...rest} />;
});

export const PdInputPercentage = React.forwardRef<TextInput, tPdInputPercentageProps>((props, ref) => {
  const { value, onChangeNumber, isDisabled, ...rest } = props;

  const handleOnChangeNumber = (newValue: number | null) => {
    if (newValue === null) onChangeNumber?.(newValue);
    if (newValue !== null && newValue >= 0 && newValue <= 100 && !isDisabled) {
      onChangeNumber?.(newValue);
    }
  };

  return (
    <PdInputNumber
      ref={ref}
      value={value}
      onChangeNumber={handleOnChangeNumber}
      keyboardType="numeric"
      maxLength={5}
      isDisabled={isDisabled}
      {...rest}
    />
  );
});

export const PdDateRangeInput = React.forwardRef<TextInput, tPdRangeDateInputProps>((props, ref) => {
  const { style, textInputStyle, period, onChange, isDisabled, ...rest } = props;

  const handleOnChangeStartPeriod = (date: Date, event: SyntheticEvent) => {
    onChange([date, period[1]], event);
  };

  const handleOnChangeEndPeriod = (date: Date, event: SyntheticEvent) => {
    onChange([period[0], date], event);
  };

  return (
    <PdInputGroup style={[styles.pdDateRangeInputContainer, style]}>
      <ReactDatePicker
        selectsStart
        selected={period[0]}
        startDate={period[0]}
        endDate={period[1]}
        onChange={handleOnChangeStartPeriod}
        dateFormat="MM/dd/yyyy"
        customInput={
          <PdInput
            ref={ref}
            style={[styles.pdInputGroupInput, isDisabled ? styles.pdDisabledInput : {}, textInputStyle]}
          />
        }
        disabled={isDisabled}
        {...rest}
      />
      <PdDivider style={styles.pdDateRangeInputDivider} />
      <ReactDatePicker
        selectsEnd
        selected={period[1]}
        startDate={period[0]}
        endDate={period[1]}
        minDate={period[0]}
        onChange={handleOnChangeEndPeriod}
        dateFormat="MM/dd/yyyy"
        customInput={
          <PdInput style={[styles.pdInputGroupInput, isDisabled ? styles.pdDisabledInput : {}, textInputStyle]} />
        }
        disabled={isDisabled}
        {...rest}
      />
    </PdInputGroup>
  );
});

const styles = StyleSheet.create({
  pdDateRangeInputContainer: {
    alignItems: "center",
    borderColor: kColors.gray300,
    borderWidth: 1,
    flexDirection: "row",
  },
  pdDateRangeInputDivider: {
    height: "60%",
    marginHorizontal: 2 * kStyleConst.kSpacing,
    width: StyleSheet.hairlineWidth,
  },
  pdDisabledInput: {
    opacity: 0.4,
  },
  pdInput: {
    borderColor: kColors.grayPink,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    color: kColors.brandDark,
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: "300",
    letterSpacing: 1.1,
    minHeight: 34,
    paddingHorizontal: 2 * kStyleConst.kSpacing,
    paddingVertical: 1.5 * kStyleConst.kSpacing,
    width: "100%",
  },
  pdInputGroup: {
    alignItems: "center",
    borderColor: kColors.grayPink,
    borderRadius: kStyleConst.kSpacing,
    borderWidth: 1,
    flexDirection: "row",
    width: "100%",
  },
  pdInputGroupInput: {
    borderWidth: 0,
    flex: 1,
  },
  pdInputLeftAddon: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  pdInputMultiple: {
    flexWrap: "wrap",
  },
  pdInputMultipleItem: {
    margin: kStyleConst.kSpacing,
  },
  pdInputMultipleItemRemoveIcon: {
    height: 8,
    width: 8,
  },
  pdInputRightAddon: {
    marginRight: kStyleConst.kSpacing * 2,
  },
});
